/**
 * Six boxes for a six-digit code — `design/mockups/33-rowted-admin` board 1.
 *
 * One `<input>` per digit rather than one box with letter-spacing, because
 * every phone keyboard and every password manager treats them differently and
 * the six-box form is the one people recognise. What makes it bearable to use
 * is the handling around it: typing moves on, backspace on an empty box moves
 * back, and a pasted code fills the row whichever box was focused.
 *
 * `autoComplete="one-time-code"` sits on the first box only. On iOS the
 * keyboard suggestion fills that box with the whole code, which the paste path
 * below then spreads — putting it on all six makes the suggestion appear six
 * times and fill one character each.
 */
import { useEffect, useRef } from 'react'
import { cn } from '../lib/cn.js'

export interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  /** Fired when the last box is filled, so the form can submit itself. */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
  label?: string
}

export function CodeInput({
  value, onChange, onComplete, length = 6, disabled, autoFocus, label = 'Code',
}: CodeInputProps) {
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  useEffect(() => {
    if (autoFocus) boxes.current[0]?.focus()
  }, [autoFocus])

  const write = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
    /* Focus the first empty box, or the last one when it is full. */
    boxes.current[Math.min(clean.length, length - 1)]?.focus()
  }

  return (
    <div role="group" aria-label={label} className="flex gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(node) => { boxes.current[index] = node }}
          value={digit.trim()}
          disabled={disabled}
          inputMode="numeric"
          /* Not `maxLength={1}`: a paste has to reach `onChange` whole. */
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`${label} digit ${index + 1}`}
          onChange={(event) => {
            const typed = event.target.value.replace(/\D/g, '')
            if (typed.length > 1) {
              /* A paste, or the phone keyboard's own suggestion. */
              write(typed)
              return
            }
            const next = value.padEnd(length, ' ').split('')
            next[index] = typed || ' '
            write(next.join('').trimEnd())
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !digit.trim() && index > 0) {
              event.preventDefault()
              const next = value.padEnd(length, ' ').split('')
              next[index - 1] = ' '
              onChange(next.join('').trimEnd())
              boxes.current[index - 1]?.focus()
            }
            if (event.key === 'ArrowLeft' && index > 0) boxes.current[index - 1]?.focus()
            if (event.key === 'ArrowRight' && index < length - 1) {
              boxes.current[index + 1]?.focus()
            }
          }}
          className={cn(
            'h-[52px] min-w-0 flex-1 rounded-field border text-center text-[22px] font-bold',
            'tnum text-text focus:outline-none',
            'border-line bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-ring',
            disabled && 'opacity-50')}
        />
      ))}
    </div>
  )
}
